import MeetingTypeSelect from "app/modules/shared/selectComponents/meetingTypeSelect/MeetingTypeSelect";
import DateTimeInput from "cpl-react-common/components/Inputs/DateTimeInput";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { ReactNode } from "react";
import { GetAttendeeTypeName, GetMeetingSelect } from "./MeetingFormUtils";
import { MeetingModel, NewMeetingModel } from "services/api/cplapi/CPLAPI";
import { MeetingAttendeeTypes } from "global/enums";
import Autosave from "app/modules/shared/autosave/Autosave";
import * as Yup from "yup";

type MeetingFormProps = {
  getFormButtons?: () => ReactNode;
  getActionButtons?: (values: MeetingModel | NewMeetingModel) => ReactNode;
  handleSubmit: (values: MeetingModel | NewMeetingModel) => void;
  initialValues: MeetingModel | NewMeetingModel;
  readonly: boolean;
  AUTOSAVE_FORM_KEY?: string;
  meetingAttendeeType: MeetingAttendeeTypes;
  setMeetingAttendeeType: React.Dispatch<React.SetStateAction<MeetingAttendeeTypes>>;
};

const getValidationSchema = () =>
  Yup.object().shape(
    {
      title: Yup.string().required("Title is required"),
      meetingTypeID: Yup.number().required("Meeting type is required"),
      meetingDate: Yup.date().required("Meeting must have a valid date"),
      endDate: Yup.date()
        .required("End date must be valid")
        .test("is-later-than-start-date", "End date must be later than start date", (value, testContext) => {
          const { meetingDate } = testContext.parent;
          return value != null && value > meetingDate;
        }),
    },
    []
  );

const MeetingForm = (props: MeetingFormProps) => {
  return (
    <Formik
      initialValues={props.initialValues}
      validationSchema={getValidationSchema}
      onSubmit={(values) => {
        props.handleSubmit(values);
      }}
      enableReinitialize={true}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form>
            {props.AUTOSAVE_FORM_KEY && <Autosave formKey={props.AUTOSAVE_FORM_KEY} />}
            <div className="row mb-6">
              <label className="form-label">Attendee Type</label>
              <div>
                {Object.keys(MeetingAttendeeTypes)
                  .filter((key) => isNaN(Number(key)))
                  .map((key) => {
                    const attendeeType = MeetingAttendeeTypes[key as keyof typeof MeetingAttendeeTypes];
                    return (
                      <label key={key} className="p-4">
                        <Field
                          data-test-id={`meeting-form__attendee-type-radio-${attendeeType}`}
                          className="form-check-input attendeetype-radio-button"
                          type="radio"
                          onChange={(selection: React.ChangeEvent<HTMLInputElement>) => {
                            props.setMeetingAttendeeType(parseInt(selection.target.value));
                          }}
                          checked={props.meetingAttendeeType === attendeeType}
                          name="attendeeType"
                          value={attendeeType}
                        />
                        {GetAttendeeTypeName(attendeeType)}
                      </label>
                    );
                  })}
              </div>
            </div>
            {GetMeetingSelect(props.meetingAttendeeType, values, setFieldValue)}
            <div className="row mb-6">
              <label className="form-label">Title</label>
              <div>
                <Field
                  data-test-id="meeting-form__title-input"
                  className="form-control form-control-solid"
                  placeholder="Title..."
                  name="title"
                  disabled={props.readonly}
                />
              </div>
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <ErrorMessage name="title" />
                </div>
              </div>
            </div>
            <div className="row mb-6">
              <label className="col-lg-4 form-label">Meeting Type</label>
              <div className="col-lg-8">
                <MeetingTypeSelect
                  data-test-id="meeting-form__meeting-type-select"
                  defaultSelectedValue={values.meetingTypeID}
                  handleChange={(selection) => {
                    setFieldValue("meetingTypeID", selection.value);
                  }}
                  disabled={props.readonly}
                  enableReinitialise
                />
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <ErrorMessage name="meetingTypeID" />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-6">
              <label className="col-lg-4 form-label">Meeting Date</label>
              <div className="col-lg-8">
                <div className="form-control form-control-solid">
                  <DateTimeInput
                    data-test-id="meeting-form__meeting-date-input"
                    date={values.meetingDate}
                    handleChange={(newDate) => {
                      setFieldValue("meetingDate", newDate);
                    }}
                    disabled={false}
                  />
                </div>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <ErrorMessage name="meetingDate" />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-6">
              <label className="col-lg-4 form-label">End Date</label>
              <div className="col-lg-8">
                <div className="form-control form-control-solid">
                  <DateTimeInput
                    data-test-id="meeting-form__end-date-input"
                    date={values.endDate}
                    handleChange={(newDate) => {
                      setFieldValue("endDate", newDate);
                    }}
                    disabled={false}
                  />
                </div>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <ErrorMessage name="endDate" />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-6 button-row">{props.getActionButtons?.(values)}</div>
            <div className="row mb-6 button-row">{props.getFormButtons?.()}</div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MeetingForm;
